/**
 * Afișarea și descărcarea documentelor juridice (regulament + informare GDPR) din SURSA UNICĂ legal-docs.js.
 * - Dacă regulamentul a fost blocat la notar (settings.legalDocs.locked), se afișează versiunea blocată.
 * - Altfel se generează din șablonul aprobat, cu datele curente (ciornă).
 * Aceleași funcții servesc comerciantul, clientul și linkul public (/m/{slug}?doc=regulament|gdpr).
 */
import {
  renderLegalDoc, templateIdsFor, TEMPLATE_REGISTRY, LEGAL_CSS, escapeLegal, roDate, sha256Hex,
  buildLegalContext, missingLegalFields, consentTexts
} from './legal-docs.js?v=82.86';

const KIND = { rules: 'Regulament oficial', gdpr: 'Informare GDPR completă' };

/** Documentul care se afișează acum, sau null dacă tipul campaniei nu are încă șablon aprobat. */
export function getDisplayDoc(kind, s = {}) {
  const ids = templateIdsFor(s);
  const id = ids[kind];
  if (!id || TEMPLATE_REGISTRY[id]?.status !== 'approved') return null;
  const ld = s.legalDocs;
  if (ld && ld.locked === true && ld[kind] && ld[kind].html) {
    return {
      locked: true, templateId: ld[kind].templateId, version: ld[kind].version, hash: ld[kind].hash,
      html: sanitizeLegalHtml(ld[kind].html), edited: ld[kind].edited === true, lockedAt: ld.lockedAt || ''
    };
  }
  const d = renderLegalDoc(id, s);
  return d ? { ...d, locked: false } : null;
}

export function usesLegalTemplates(s = {}) {
  return !!getDisplayDoc('rules', s);
}

/** Doar etichete de text sigure (versiunea blocată vine din baza de date). */
export function sanitizeLegalHtml(html) {
  const allowed = /^(h1|h2|h3|h4|p|b|strong|em|i|u|ul|ol|li|table|thead|tbody|tr|th|td|br|div|span)$/i;
  return String(html || '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|embed|svg|math)[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<\/?([a-z0-9]+)([^>]*)>/gi, (m, tag, attrs) => {
      if (!allowed.test(tag)) return '';
      const close = m.startsWith('</');
      if (close) return `</${tag.toLowerCase()}>`;
      const cls = /\sclass\s*=\s*"([a-z0-9_\- ]{0,80})"/i.exec(attrs || '');
      const self = /\/\s*>$/.test(m) ? '/' : '';
      return `<${tag.toLowerCase()}${cls ? ` class="${cls[1]}"` : ''}${self}>`;
    });
}

export function publicDocUrl(s = {}, kind = 'rules') {
  const slug = String(s.publicSlug || '').trim();
  if (!slug) return '';
  return `${location.origin}/m/${encodeURIComponent(slug)}?doc=${kind === 'gdpr' ? 'gdpr' : 'regulament'}`;
}

export function ensureCss() {
  if (document.getElementById('sjLegalCss')) return;
  const st = document.createElement('style');
  st.id = 'sjLegalCss';
  st.textContent = `${LEGAL_CSS}
.sj-lg-ov{position:fixed;inset:0;z-index:2147483000;display:none;align-items:flex-end;justify-content:center;background:rgba(15,20,18,.55);padding:8px;box-sizing:border-box}
.sj-lg-ov.is-open{display:flex}
@media (min-width:640px){.sj-lg-ov{align-items:center}}
.sj-lg-sheet{width:100%;max-width:760px;height:calc(100dvh - 16px);display:flex;flex-direction:column;background:#FFFFFF;border-radius:22px;box-shadow:0 20px 50px rgba(0,0,0,.3);overflow:hidden}
.sj-lg-head{flex:none;display:flex;align-items:center;gap:10px;padding:12px 14px;border-bottom:1px solid #EEEDE8}
.sj-lg-head b{flex:1;font-size:16px;color:#17201C}
.sj-lg-ov button.sj-lg-x{flex:none;width:40px!important;height:40px!important;min-height:40px!important;margin:0!important;padding:0!important;border-radius:12px!important;border:0!important;background:#F0EEE8!important;background-image:none!important;color:#17201C!important;font-size:22px!important;box-shadow:none!important}
.sj-lg-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px 18px 24px}
.sj-lg-foot{flex:none;display:flex;flex-wrap:wrap;gap:8px;padding:10px 14px;border-top:1px solid #EEEDE8;background:#FAFAF7}
.sj-lg-ov .sj-lg-foot button{flex:1 1 30%;width:auto!important;min-height:44px!important;height:auto!important;margin:0!important;padding:8px 10px!important;border-radius:12px!important;border:1px solid #E4E2DC!important;background:#FFFFFF!important;background-image:none!important;color:#17201C!important;font-size:14px!important;font-weight:700!important;box-shadow:none!important;text-transform:none!important;letter-spacing:0!important}
.sj-lg-ov .sj-lg-foot a.sj-lg-btn{flex:1 1 30%;display:flex;align-items:center;justify-content:center;min-height:44px;padding:8px 10px;border-radius:12px;border:1px solid #E4E2DC;background:#FFFFFF;color:#17201C;font-size:14px;font-weight:700;text-decoration:none}
.sj-lg-ov .sj-lg-foot button.is-main{background:#17201C!important;color:#FFFFFF!important;border-color:#17201C!important}
.sj-lg-draft{margin:0 0 12px;padding:10px 12px;border-radius:12px;background:#FFF6E5;border:1px solid #F3D9A4;color:#5C3D00;font-size:13px;line-height:1.4}
.sj-lg-body [contenteditable="true"]{outline:2px dashed #C77700;outline-offset:4px;border-radius:6px}
`;
  document.head.appendChild(st);
}

function sheet() {
  ensureCss();
  let ov = document.getElementById('sjLegalDoc');
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = 'sjLegalDoc';
  ov.className = 'sj-lg-ov';
  ov.setAttribute('role', 'dialog');
  ov.setAttribute('aria-modal', 'true');
  ov.innerHTML = '<div class="sj-lg-sheet"><div class="sj-lg-head"><b id="sjLegalTitle"></b><button type="button" class="sj-lg-x" aria-label="Închide">×</button></div><div class="sj-lg-body" id="sjLegalBody"></div><div class="sj-lg-foot" id="sjLegalFoot"></div></div>';
  ov.addEventListener('click', (e) => {
    if (e.target === ov || e.target.closest('.sj-lg-x')) closeLegalDoc();
  });
  document.body.appendChild(ov);
  return ov;
}

export function closeLegalDoc() {
  const ov = document.getElementById('sjLegalDoc');
  if (ov) ov.classList.remove('is-open');
  document.documentElement.style.overflow = '';
}

function metaLine(d, s) {
  const na = s.notaryAct || {};
  const parts = [`Versiune șablon ${escapeLegal(d.version || '')}`];
  if (d.locked) {
    parts.push(`blocată la autentificare${d.lockedAt ? ` (${escapeLegal(roDate(d.lockedAt))})` : ''}`);
    if (d.hash) parts.push(`amprentă ${escapeLegal(String(d.hash).slice(0, 12))}`);
  }
  if (na.confirmed && na.number) parts.push(`autentificat: nr. ${escapeLegal(na.number)}${na.date ? ` din ${escapeLegal(roDate(na.date))}` : ''}${na.office ? `, notar public ${escapeLegal(na.office)}` : ''}`);
  return `<p class="lg-meta">${parts.join(' · ')}</p>`;
}

/**
 * Deschide documentul într-un modal propriu (scroll intern, buton de închidere).
 * opts.merchant = true afișează și avertismentul de ciornă cu datele lipsă.
 */
export function openLegalDoc(kind, s = {}, opts = {}) {
  // regulamentul propriu al comerciantului (PDF încărcat la pornire) are prioritate
  if (kind === 'rules' && s && s.ownRules && Array.isArray(s.ownRules.pages) && s.ownRules.pages.length) {
    import('./legal-notary.js?v=82.86').then((m) => m.openOwnRules(s)).catch(() => {});
    return true;
  }
  const d = getDisplayDoc(kind, s);
  if (!d) return false;
  const ov = sheet();
  ov.querySelector('#sjLegalTitle').textContent = KIND[kind] || 'Document';
  let warn = '';
  if (!d.locked) {
    const miss = d.missing || [];
    if (opts.merchant && miss.length) {
      warn = `<div class="sj-lg-draft"><b>Ciornă — lipsesc ${miss.length} date:</b> ${miss.map((m) => escapeLegal(m.label)).join('; ')}. Completează-le înainte de a duce regulamentul la notar.</div>`;
    } else if (opts.merchant) {
      warn = '<div class="sj-lg-draft"><b>Gata de dus la notar.</b> După autentificare, introduci numărul încheierii la pornirea campaniei, iar textul se blochează.</div>';
    }
  }
  ov.querySelector('#sjLegalBody').innerHTML = `${warn}<div class="lg-doc">${d.html}</div>${metaLine(d, s)}`;
  // acte adiționale încărcate în timpul campaniei (peste regulamentul generat)
  if (kind === 'rules' && Array.isArray(s.ownRulesAddenda) && s.ownRulesAddenda.length) {
    import('./legal-notary.js?v=82.86').then((m) => {
      const body = ov.querySelector('#sjLegalBody');
      const add = m.buildAddendaBlock(s);
      if (add) { const h = document.createElement('h3'); h.className = 'sj-lg-ownh'; h.textContent = 'Regulamentul'; body.prepend(add, h); }
      const hist = m.buildHistoryBlock(s);
      if (hist) body.appendChild(hist);
    }).catch(() => {});
  }
  const foot = ov.querySelector('#sjLegalFoot');
  foot.replaceChildren();
  const btn = (label, fn, main) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    if (main) b.className = 'is-main';
    b.addEventListener('click', fn);
    foot.appendChild(b);
  };
  btn('⬇️ Word', () => downloadWord(kind, s));
  btn('🖨️ PDF', () => printPdf(kind, s));
  const url = publicDocUrl(s, kind);
  if (url) {
    btn('🔗 Copiază linkul', async () => {
      try { await navigator.clipboard.writeText(url); toast('Linkul public a fost copiat.'); } catch (_) { window.prompt('Linkul public:', url); }
    });
  }
  btn('Închide', closeLegalDoc, true);
  ov.classList.add('is-open');
  ov.querySelector('#sjLegalBody').scrollTop = 0;
  document.documentElement.style.overflow = 'hidden';
  return true;
}

function toast(msg) {
  try { import('../utils/ui.js?v=82.86').then((m) => m.showSnackbar?.(msg, 'success')).catch(() => {}); } catch (_) { /* ignore */ }
}

function docTitle(kind, s) {
  const name = buildLegalContext(s).campaign.name || 'Campanie';
  return `${kind === 'gdpr' ? 'Informare GDPR' : 'Regulament oficial'} - ${name}`;
}

/** HTML complet pentru Word și PDF (include rândurile de tipar: antet, semnătură, formularul de completat). */
function printableHtml(kind, s) {
  const d = getDisplayDoc(kind, s);
  if (!d) return '';
  if (d.locked) return d.html; // versiunea autentificată, exact cum a fost blocată
  const full = renderLegalDoc(templateIdsFor(s)[kind], s);
  return full ? full.printHtml : d.html;
}

export function downloadWord(kind, s = {}) {
  const body = printableHtml(kind, s);
  if (!body) return;
  const title = docTitle(kind, s);
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${escapeLegal(title)}</title><style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt}${LEGAL_CSS}</style></head><body><div class="lg-doc">${body}</div></body></html>`;
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${title.replace(/[\\/:*?"<>|]+/g, '')}.doc`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}

export function printPdf(kind, s = {}) {
  const body = printableHtml(kind, s);
  if (!body) return;
  ensureCss();
  let box = document.getElementById('sjPrintReport');
  if (!box) {
    box = document.createElement('div');
    box.id = 'sjPrintReport';
    document.body.appendChild(box);
  }
  box.innerHTML = `<div class="lg-doc">${body}</div>`;
  const oldTitle = document.title;
  document.title = docTitle(kind, s);
  document.body.classList.add('sj-printing-report');
  const ov = document.getElementById('sjLegalDoc');
  const wasOpen = ov?.classList.contains('is-open');
  if (wasOpen) ov.classList.remove('is-open');
  const done = () => {
    document.body.classList.remove('sj-printing-report');
    document.title = oldTitle;
    if (wasOpen) ov.classList.add('is-open');
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  toast('Alege „Salvează ca PDF” în fereastra care se deschide.');
  setTimeout(() => { try { window.print(); } catch (_) { done(); } }, 300);
}

/** Pentru dovada acceptării: versiunea + amprenta documentelor arătate clientului. */
export async function shownDocsProof(s = {}) {
  const out = {};
  for (const kind of ['rules', 'gdpr']) {
    const d = getDisplayDoc(kind, s);
    if (!d) continue;
    out[kind] = {
      templateId: d.templateId,
      version: d.version,
      locked: !!d.locked,
      hash: d.locked ? d.hash : await sha256Hex(d.text || '')
    };
  }
  return out;
}

export { consentTexts, buildLegalContext, missingLegalFields };

/** Linkul public: /m/{slug}?doc=regulament|gdpr deschide documentul direct. */
export function openDocFromUrl(s = {}) {
  try {
    const q = new URLSearchParams(location.search);
    const doc = q.get('doc');
    if (!doc) return false;
    if (doc === 'castigatori') return openWinners(s);
    return openLegalDoc(doc === 'gdpr' ? 'gdpr' : 'rules', s);
  } catch (_) { return false; }
}

/** Câștigătorii publicați (art. 9): nume și premii, fără date de contact. */
export function openWinners(s = {}) {
  const r = s.drawResults;
  if (!r || !Array.isArray(r.winners) || !r.winners.length) return false;
  const ov = sheet();
  ov.querySelector('#sjLegalTitle').textContent = '🏆 Câștigătorii';
  const medal = ['🥇', '🥈', '🥉'];
  ov.querySelector('#sjLegalBody').innerHTML = `<div class="lg-doc">
    <h1 class="lg-title">Câștigătorii campaniei<br/>„${escapeLegal(r.campaignName || '')}”</h1>
    <p class="lg-sub">Extragere efectuată pe ${escapeLegal(new Date(r.drawnAt).toLocaleDateString('ro-RO'))}. Publicare conform art. 42 alin. (2) din OG nr. 99/2000.</p>
    <div class="lg-tablewrap"><table class="lg-table lg-table--prizes"><thead><tr><th>Loc</th><th>Câștigător</th><th>Premiu</th></tr></thead><tbody>
    ${r.winners.sort((a, b) => a.place - b.place).map((w) => `<tr><td>${medal[w.place - 1] || ''} ${w.place}</td><td>${escapeLegal(w.name)}</td><td>${escapeLegal(w.prize)}</td></tr>`).join('')}
    </tbody></table></div><p class="lg-p">Felicitări câștigătorilor! Mulțumim tuturor participanților.</p></div>`;
  const foot = ov.querySelector('#sjLegalFoot');
  foot.replaceChildren();
  const b = document.createElement('button'); b.type = 'button'; b.className = 'is-main'; b.textContent = 'Închide'; b.addEventListener('click', closeLegalDoc); foot.appendChild(b);
  ov.classList.add('is-open');
  document.documentElement.style.overflow = 'hidden';
  return true;
}

/** Banner discret pe pagina publică, cât timp există câștigători publicați. */
export function mountWinnersBanner(s = {}) {
  const r = s.drawResults;
  let el = document.getElementById('sjWinnersBanner');
  if (!r || !Array.isArray(r.winners) || !r.winners.length) { if (el) el.remove(); return; }
  ensureCss();
  if (!document.getElementById('sjWinnersCss')) {
    const st = document.createElement('style');
    st.id = 'sjWinnersCss';
    st.textContent = '#sjWinnersBanner{position:fixed;left:50%;top:12px;transform:translateX(-50%);z-index:9000;display:flex;align-items:center;gap:8px;max-width:calc(100vw - 24px);padding:8px 8px 8px 14px;border-radius:999px;background:#17201C;color:#FFF;font-size:14px;font-weight:700;box-shadow:0 8px 24px rgba(0,0,0,.25)}#sjWinnersBanner button{width:auto!important;min-height:34px!important;height:34px!important;margin:0!important;padding:0 12px!important;border-radius:999px!important;border:0!important;background:#FFD24D!important;background-image:none!important;color:#17201C!important;font-size:13px!important;font-weight:800!important;box-shadow:none!important}#sjWinnersBanner button.x{background:rgba(255,255,255,.15)!important;color:#FFF!important;padding:0 10px!important}';
    document.head.appendChild(st);
  }
  try { if (sessionStorage.getItem(`sj_wb_${r.runId}`) === '1') return; } catch (_) { /* ignore */ }
  if (!el) {
    el = document.createElement('div');
    el.id = 'sjWinnersBanner';
    document.body.appendChild(el);
  }
  el.innerHTML = '<span>🏆 Câștigătorii au fost anunțați</span><button type="button" class="v">Vezi</button><button type="button" class="x" aria-label="Închide">✕</button>';
  el.querySelector('.v').addEventListener('click', () => openWinners(s));
  el.querySelector('.x').addEventListener('click', () => { el.remove(); try { sessionStorage.setItem(`sj_wb_${r.runId}`, '1'); } catch (_) { /* ignore */ } });
}
